import { CopyOutlined, SyncOutlined } from "@ant-design/icons";
import { App, Button, Flex } from "antd";
import { FC, useCallback } from "react";
import { createUniqueId } from "tm-shared/utils";

import { TaskProgressDisplay } from "@/domains/tasks/components/TaskProgressDisplay";
import { NetworkIndicator } from "@/features/NetworkIndicator";
import { QueueTaskStatus } from "@/hooks/core/useAsyncTaskQueue";
import { useAppDispatch, useOfflineModeSelector } from "@/hooks/redux";
import { useQueueInfo } from "@/hooks/requestQueue/useQueueInfo";
import { useUserPayload } from "@/hooks/requestQueue/useUserPayload";
import { BaseModal } from "@/shared/ui/BaseModal";
import { PrimaryText } from "@/shared/ui/Typography";
import { restartRequestQueue } from "@/store/slices/offline";
import { RequestQueueItem } from "@/types/requestQueue";
import { copyObjectToClipboard, mapRequestQueueItemForCopy } from "@/utils";

export interface TaskProgressModalPayload {
    requestQueueItem: RequestQueueItem;
}

interface TaskProgressModalProps {
    onClose: () => void;
    payload: TaskProgressModalPayload;
}

export const TaskProgressModal: FC<TaskProgressModalProps> = ({
    onClose,
    payload,
}) => {
    const { notification } = App.useApp();
    const offlineMode = useOfflineModeSelector();
    const dispatch = useAppDispatch();

    const { isRunning: isRunningQueue } = useQueueInfo();

    const { requestQueueItem: queueItem } = payload;

    const { progress, commands, id } = queueItem;
    const isRunning = progress?.isRunning;

    const { userPayload } = useUserPayload();

    const handleRetry = useCallback(() => {
        dispatch(restartRequestQueue(id));
    }, [dispatch, id]);

    const handleCopyQueue = useCallback(() => {
        const result = {
            user: userPayload,
            queue: mapRequestQueueItemForCopy(queueItem),
        };

        copyObjectToClipboard(result, notification);
    }, [notification, queueItem, userPayload]);

    const progressDisplayData = progress
        ? progress.tasks
        : commands.map(({ title }) => ({
              id: createUniqueId(),
              label: title,
              status: QueueTaskStatus.Pending,
          }));

    const error =
        progress?.lastErrorIndex !== undefined
            ? progress?.tasks?.[progress?.lastErrorIndex]?.error
            : undefined;

    return (
        <BaseModal
            open
            onClose={onClose}
            title={
                isRunning
                    ? "Завантаження даних на сервер"
                    : "Задача знаходиться в черзі"
            }
            actions={null}
            mask={{ closable: true }}
            icon={<NetworkIndicator />}
        >
            {isRunning ? (
                <div>
                    <PrimaryText textAlign="center">
                        Не вимикайте екран
                    </PrimaryText>
                    <PrimaryText textAlign="center">
                        Не закривайте застосунок
                    </PrimaryText>
                    <PrimaryText textAlign="center">
                        Не оновлюйте сторінку
                    </PrimaryText>
                </div>
            ) : (
                <Flex vertical gap={8}>
                    <Flex gap={8} justify="space-between">
                        <Button
                            type="primary"
                            icon={<SyncOutlined />}
                            disabled={offlineMode || isRunningQueue}
                            onClick={handleRetry}
                        >
                            Синхронізувати
                        </Button>

                        <Button
                            shape="circle"
                            icon={<CopyOutlined style={{ fontSize: 22 }} />}
                            onClick={handleCopyQueue}
                            type="text"
                        />
                    </Flex>

                    {error && (
                        <PrimaryText textColor="error">{error}</PrimaryText>
                    )}
                </Flex>
            )}

            <TaskProgressDisplay tasks={progressDisplayData} />
        </BaseModal>
    );
};
